
import React, { useState, useRef } from 'react';
import { useData } from '../../context/DataContext';
import {
  Save, RefreshCw, Globe, Palette, MapPin, Share2, Search, HelpCircle,
  Shield, Server, CreditCard, Bell, Database, PenTool, Layout, Phone, Lock, Upload, Image as ImageIcon, X, Scale, Key, Cloud, MoveUp, MoveDown, Plus, Download, Copy, FileJson, Terminal, FileCode, Truck, Trash2, Settings, Zap, Leaf, Disc, Gauge, AlertTriangle, MessageCircle, Play, CheckCircle, XCircle
} from 'lucide-react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { SiteSettings, NavLink, ServiceArea, FAQItem } from '../../types';

type SettingsSection =
  | 'general' | 'navigation' | 'pages' | 'regional' | 'appearance' | 'social' | 'seo'
  | 'maintenance' | 'integrations' | 'business' | 'legal' | 'data' | 'inventory' | 'promotion' | 'whatsapp' | 'chatbot' | 'faq';

const SettingsManager: React.FC = () => {
  const { settings, updateSettings, updateAdminPassword, resetSettings } = useData();
  const [formData, setFormData] = useState<SiteSettings>({ ...settings });
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // State for password change
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const promoImageInputRef = useRef<HTMLInputElement>(null);
  const chatbotLogoInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateSettings(formData);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error: any) {
      console.error('Failed to save settings:', error);
      alert(`Error saving settings: ${error.message || error}`);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      setPasswordMsg('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg('Passwords do not match');
      return;
    }

    const success = await updateAdminPassword(newPassword);

    if (success) {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg('Admin password updated successfully!');
      setTimeout(() => setPasswordMsg(''), 3000);
    } else {
      setPasswordMsg('Failed to update password. Please try again.');
    }
  };

  const handleChange = (field: keyof SiteSettings, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent: keyof SiteSettings, key: string, value: any) => {
    setFormData(prev => {
      // Safely access the parent object, handling potential null values
      const parentObj = prev[parent];
      const safeParent = (parentObj && typeof parentObj === 'object') ? parentObj : {};

      return {
        ...prev,
        [parent]: {
          ...safeParent,
          [key]: value
        }
      };
    });
  };

  // Nav Link Management
  const updateNavLink = (id: string, field: keyof NavLink, value: any) => {
    const updatedLinks = formData.navLinks.map(link =>
      link.id === id ? { ...link, [field]: value } : link
    );
    handleChange('navLinks', updatedLinks);
  };

  const moveLink = (index: number, direction: 'up' | 'down') => {
    const newLinks = [...formData.navLinks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newLinks.length) {
      // Swap order values
      const tempOrder = newLinks[index].order;
      newLinks[index].order = newLinks[targetIndex].order;
      newLinks[targetIndex].order = tempOrder;

      // Swap elements
      [newLinks[index], newLinks[targetIndex]] = [newLinks[targetIndex], newLinks[index]];
      handleChange('navLinks', newLinks);
    }
  };

  const deleteLink = (id: string) => {
    handleChange('navLinks', formData.navLinks.filter(l => l.id !== id));
  };

  const addLink = () => {
    const newLink: NavLink = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'New Link',
      path: '/',
      order: formData.navLinks.length
    };
    handleChange('navLinks', [...formData.navLinks, newLink]);
  };

  // Service Area Management
  const addServiceArea = () => {
    const newArea: ServiceArea = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'New Location',
      isHeadquarters: false,
      order: (formData.serviceAreas || []).length
    };
    handleChange('serviceAreas', [...(formData.serviceAreas || []), newArea]);
  };

  const updateServiceArea = (id: string, field: keyof ServiceArea, value: any) => {
    const updated = (formData.serviceAreas || []).map(area =>
      area.id === id ? { ...area, [field]: value } : area
    );
    handleChange('serviceAreas', updated);
  };

  const deleteServiceArea = (id: string) => {
    handleChange('serviceAreas', (formData.serviceAreas || []).filter(a => a.id !== id));
  };

  const moveServiceArea = (index: number, direction: 'up' | 'down') => {
    const items = [...(formData.serviceAreas || [])];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < items.length) {
      const temp = items[index].order;
      items[index].order = items[targetIndex].order;
      items[targetIndex].order = temp;
      [items[index], items[targetIndex]] = [items[targetIndex], items[index]];
      handleChange('serviceAreas', items);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: keyof SiteSettings) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);

      // 1. Try Cloudinary Upload if configured
      if (settings.cloudinaryCloudName && settings.cloudinaryUploadPreset) {
        try {
          const data = new FormData();
          data.append('file', file);
          data.append('upload_preset', settings.cloudinaryUploadPreset);
          const res = await fetch(`https://api.cloudinary.com/v1_1/${settings.cloudinaryCloudName}/image/upload`, {
            method: 'POST',
            body: data
          });
          const json = await res.json();
          if (json.secure_url) {
            handleChange(field, json.secure_url);
            setUploading(false);
            return;
          }
        } catch (error) {
          console.error("Cloud upload failed, falling back to local compression", error);
        }
      }

      // 2. Fallback to Local Compression (FileReader)
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 400; // Resize logos to max 400px

          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            // Use original format type or PNG for transparency, but compressed
            const fileType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
            const dataUrl = canvas.toDataURL(fileType, 0.8);
            handleChange(field, dataUrl);
            setUploading(false);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (field: keyof SiteSettings) => {
    handleChange(field, '');
    if (field === 'logoUrl' && logoInputRef.current) logoInputRef.current.value = '';
    if (field === 'faviconUrl' && faviconInputRef.current) faviconInputRef.current.value = '';
  };

  const exportConfig = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(formData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `automax_settings_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const importConfig = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target?.result as string);
          if (json && typeof json === 'object') {
            setFormData({ ...formData, ...json });
            alert("Configuration imported! Remember to click 'Save Changes' to apply permanently.");
          }
        } catch (error) {
          alert("Invalid configuration file.");
        }
      };
      reader.readAsText(file);
    }
  };

  // Generate the full config.ts file for deployment
  const downloadConfigFile = () => {
    const fileContent = `import { SiteSettings } from './types';\n\nexport const defaultSettings: SiteSettings = ${JSON.stringify(formData, null, 2)};`;
    const blob = new Blob([fileContent], { type: 'text/typescript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'config.ts';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  const testGeminiConnection = async () => {
    if (!formData.chatbotAIConfig?.apiKey) {
      alert("Please enter a Gemini API Key first.");
      return;
    }

    setTestingAI(true);
    setTestResult(null);

    try {
      const genAI = new GoogleGenerativeAI(formData.chatbotAIConfig.apiKey);
      const modelName = formData.chatbotAIConfig.model || 'gemini-2.5-pro';
      const model = genAI.getGenerativeModel({ model: modelName });

      const result = await model.generateContent("Hello! Respond with 'Connected' if you receive this.");
      const response = await result.response;
      const text = response.text();

      setTestResult({
        success: true,
        message: `Success! Model (${modelName}) responded: "${text}"`
      });
    } catch (error: any) {
      console.error("Gemini Connection Test Failed:", error);
      setTestResult({
        success: false,
        message: `Connection Failed: ${error.message || error}`
      });
    } finally {
      setTestingAI(false);
    }
  };

  const menuItems = [
    { id: 'general', label: 'General Info', icon: <Globe size={18} /> },
    { id: 'promotion', label: 'Homepage Promotion', icon: <Zap size={18} /> },
    { id: 'navigation', label: 'Navigation Menu', icon: <Layout size={18} /> },
    { id: 'pages', label: 'Content & Pages', icon: <PenTool size={18} /> },
    { id: 'inventory', label: 'Inventory Settings', icon: <Truck size={18} /> },
    { id: 'business', label: 'Business Hours', icon: <Phone size={18} /> },
    { id: 'appearance', label: 'Appearance', icon: <Palette size={18} /> },
    { id: 'regional', label: 'Regional', icon: <MapPin size={18} /> },
    { id: 'social', label: 'Social Media', icon: <Share2 size={18} /> },
    { id: 'seo', label: 'SEO & Meta', icon: <Search size={18} /> },
    { id: 'legal', label: 'Legal Pages', icon: <Scale size={18} /> },
    { id: 'integrations', label: 'Integrations', icon: <CreditCard size={18} /> },
    { id: 'whatsapp', label: 'WhatsApp Widget', icon: <MessageCircle size={18} /> },
    { id: 'chatbot', label: 'AI Chatbot', icon: <Zap size={18} /> },
    { id: 'faq', label: 'FAQ Manager', icon: <HelpCircle size={18} /> },
    { id: 'maintenance', label: 'Security & Maint.', icon: <Shield size={18} /> },
    { id: 'data', label: 'Data Management', icon: <Database size={18} /> },
  ];

  const labelClass = "block text-sm font-bold text-black mb-1.5";
  const inputClass = "w-full border border-gray-300 p-2.5 rounded-sm bg-white text-black focus:border-automax-red focus:ring-1 focus:ring-automax-red outline-none shadow-sm";
  const sectionHeaderClass = "text-xl font-bold text-black border-b border-gray-200 pb-3 mb-6";

  return (
    <div className="flex flex-col h-[calc(100vh-100px)] bg-white text-black">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 bg-white p-4 border-b border-gray-100">
        <div>
          <h1 className="text-3xl font-bold text-black">Site Settings</h1>
          <p className="text-gray-600">Configure global preferences and website information.</p>
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setFormData({ ...settings })}
            className="px-4 py-2 text-black bg-white border border-gray-300 rounded-sm hover:bg-gray-50 flex items-center gap-2 font-medium"
          >
            <RefreshCw size={18} /> Discard
          </button>
          <button
            onClick={handleSubmit}
            disabled={uploading}
            className={`px-6 py-2 text-white font-bold rounded-sm flex items-center gap-2 transition-all shadow-md ${saved ? 'bg-green-600' : 'bg-automax-red hover:bg-red-700'} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Save size={18} /> {uploading ? 'Uploading...' : saved ? 'Changes Saved' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="flex flex-1 gap-6 overflow-hidden px-4 pb-4">
        {/* Sidebar */}
        <aside className="w-64 bg-white border border-gray-200 rounded-sm overflow-y-auto shadow-sm">
          <nav className="p-2 space-y-1">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id as SettingsSection)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-bold rounded-sm transition-colors ${activeSection === item.id
                  ? 'bg-red-50 text-automax-red border-l-4 border-automax-red'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-black'
                  }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <div className="flex-1 bg-white border border-gray-200 rounded-sm overflow-y-auto p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl mx-auto">

            {/* ... other sections remain the same ... */}

            {activeSection === 'general' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>General Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Company Name</label>
                    <input type="text" value={formData.siteName} onChange={e => handleChange('siteName', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Established Year</label>
                    <input type="number" value={formData.establishedYear} onChange={e => handleChange('establishedYear', Number(e.target.value))} className={inputClass} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Tagline</label>
                    <input type="text" value={formData.tagline} onChange={e => handleChange('tagline', e.target.value)} className={inputClass} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Business Description</label>
                    <textarea rows={3} value={formData.description} onChange={e => handleChange('description', e.target.value)} className={inputClass} />
                  </div>
                </div>

                <h2 className={`${sectionHeaderClass} mt-8`}>Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Main Phone</label>
                    <input type="text" value={formData.phoneNumber} onChange={e => handleChange('phoneNumber', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Main Email</label>
                    <input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Secondary Email (Optional)</label>
                    <input type="email" value={formData.secondaryEmail || ''} onChange={e => handleChange('secondaryEmail', e.target.value)} className={inputClass} placeholder="support@automax.co.tz" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>HQ Address</label>
                    <input type="text" value={formData.address} onChange={e => handleChange('address', e.target.value)} className={inputClass} />
                  </div>
                </div>
              </div>
            )}

            {/* 1.5. PROMOTION */}
            {activeSection === 'promotion' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Homepage Promotion</h2>
                <div className="bg-automax-red/5 p-4 rounded-sm border border-automax-red/20 mb-6">
                  <p className="text-sm text-automax-red flex items-center gap-2">
                    <Zap size={16} />
                    This section controls the special offer banner displayed on the homepage.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className={labelClass}>Promotion Title</label>
                    <input type="text" value={formData.promoTitle || ''} onChange={e => handleChange('promoTitle', e.target.value)} className={inputClass} placeholder="e.g. Upgrade Your Logistics Fleet" />
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Promotion Subtitle</label>
                    <textarea rows={3} value={formData.promoSubtitle || ''} onChange={e => handleChange('promoSubtitle', e.target.value)} className={inputClass} placeholder="e.g. Purchase any Euro 5 truck..." />
                  </div>

                  <div>
                    <label className={labelClass}>Tag Label</label>
                    <input type="text" value={formData.promoTag || ''} onChange={e => handleChange('promoTag', e.target.value)} className={inputClass} placeholder="e.g. Limited Time Offer" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Discount Value</label>
                      <input type="text" value={formData.promoDiscountValue || ''} onChange={e => handleChange('promoDiscountValue', e.target.value)} className={inputClass} placeholder="e.g. 0%" />
                    </div>
                    <div>
                      <label className={labelClass}>Discount Label</label>
                      <input type="text" value={formData.promoDiscountLabel || ''} onChange={e => handleChange('promoDiscountLabel', e.target.value)} className={inputClass} placeholder="e.g. Interest Rate" />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Promotion Image URL</label>
                    <div className="flex gap-2 mb-2">
                      <input type="text" value={formData.promoImage || ''} onChange={e => handleChange('promoImage', e.target.value)} className={inputClass} placeholder="https://..." />
                      {formData.promoImage && (
                        <div className="w-10 h-10 border bg-white flex items-center justify-center p-1 rounded-sm shrink-0 overflow-hidden">
                          <img src={formData.promoImage} alt="Promo" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={promoImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'promoImage')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => promoImageInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-sm hover:bg-gray-100 flex items-center justify-center gap-2 text-sm font-bold"
                      >
                        <Upload size={16} /> Upload Image
                      </button>
                      {formData.promoImage && (
                        <button
                          type="button"
                          onClick={() => removeImage('promoImage')}
                          className="bg-white border border-gray-300 text-red-600 py-2 px-3 rounded-sm hover:bg-red-50"
                          title="Remove Image"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Benefits (One per line)</label>
                    <textarea
                      rows={5}
                      value={Array.isArray(formData.promoBenefits) ? formData.promoBenefits.join('\n') : ''}
                      onChange={e => handleChange('promoBenefits', e.target.value.split('\n'))}
                      className={inputClass}
                      placeholder="Free Maintenance&#10;GPS Tracking&#10;Full Tank"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 12. WHATSAPP */}
            {activeSection === 'whatsapp' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>WhatsApp Widget Configuration</h2>
                {/* ... WhatsApp form content ... */}
                <div className="bg-green-50 p-4 rounded-sm border border-green-200 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 rounded-full text-green-600">
                      <MessageCircle size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-green-800">Direct Communication Channel</h3>
                      <p className="text-sm text-green-700 mt-1">
                        Enable a floating WhatsApp button on your site to allow customers to chat with you directly.
                        This increases conversion rates significantly.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full cursor-pointer">
                    <input
                      type="checkbox"
                      id="wa-enabled"
                      className="peer absolute w-full h-full opacity-0 z-10 cursor-pointer"
                      checked={formData.whatsappConfig?.enabled ?? true}
                      onChange={e => handleNestedChange('whatsappConfig', 'enabled', e.target.checked)}
                    />
                    <div className={`w-full h-full rounded-full shadow-inner transition-colors ${formData.whatsappConfig?.enabled ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.whatsappConfig?.enabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
                  </div>
                  <label htmlFor="wa-enabled" className="font-bold text-gray-700 cursor-pointer select-none">
                    Enable WhatsApp Widget
                  </label>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${formData.whatsappConfig?.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                  <div>
                    <label className={labelClass}>WhatsApp Number (Optional Override)</label>
                    <input
                      type="text"
                      value={formData.whatsappConfig?.phoneNumber || ''}
                      onChange={e => handleNestedChange('whatsappConfig', 'phoneNumber', e.target.value)}
                      className={inputClass}
                      placeholder={formData.phoneNumber || "+255..."}
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to use main Site Phone Number.</p>
                  </div>

                  <div>
                    <label className={labelClass}>Welcome Message</label>
                    <input
                      type="text"
                      value={formData.whatsappConfig?.message || ''}
                      onChange={e => handleNestedChange('whatsappConfig', 'message', e.target.value)}
                      className={inputClass}
                      placeholder="Hello! I'm interested in..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Pre-filled text when user opens chat.</p>
                  </div>

                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="wa-tooltip"
                        checked={formData.whatsappConfig?.showTooltip ?? true}
                        onChange={e => handleNestedChange('whatsappConfig', 'showTooltip', e.target.checked)}
                        className="rounded text-automax-red focus:ring-automax-red"
                      />
                      <label htmlFor="wa-tooltip" className="text-sm font-medium text-gray-700">Show "Chat with us" Tooltip</label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 12.5 CHATBOT */}
            {activeSection === 'chatbot' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Chatbot Configuration</h2>

                <div className="bg-blue-50 p-4 rounded-sm border border-blue-200 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 rounded-full text-blue-600">
                      <Zap size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-blue-800">Automax Assistant</h3>
                      <p className="text-sm text-blue-700 mt-1">
                        Configure the automated assistant appearance and basic settings. Scroll down to enable the powerful Gemini AI integration.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full cursor-pointer">
                    <input
                      type="checkbox"
                      id="cb-enabled"
                      className="peer absolute w-full h-full opacity-0 z-10 cursor-pointer"
                      checked={formData.chatbotConfig?.enabled ?? true}
                      onChange={e => handleNestedChange('chatbotConfig', 'enabled', e.target.checked)}
                    />
                    <div className={`w-full h-full rounded-full shadow-inner transition-colors ${formData.chatbotConfig?.enabled ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.chatbotConfig?.enabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
                  </div>
                  <label htmlFor="cb-enabled" className="font-bold text-gray-700 cursor-pointer select-none">
                    Enable Chatbot
                  </label>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${formData.chatbotConfig?.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Bot Profile Picture</label>
                    <div className="flex gap-4 items-center">
                      <div className="w-16 h-16 rounded-full border bg-gray-100 flex items-center justify-center overflow-hidden shrink-0 relative">
                        {formData.chatbotConfig?.logoUrl ? (
                          <img src={formData.chatbotConfig.logoUrl} alt="Bot" className="w-full h-full object-cover" />
                        ) : (
                          <Zap size={24} className="text-gray-400" />
                        )}
                      </div>
                      <div className="flex-1 flex gap-2">
                        <input type="text" value={formData.chatbotConfig?.logoUrl || ''} onChange={e => handleNestedChange('chatbotConfig', 'logoUrl', e.target.value)} className={inputClass} placeholder="Image URL..." />
                        <input
                          ref={chatbotLogoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            // Custom handler for nested object upload
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                handleNestedChange('chatbotConfig', 'logoUrl', ev.target?.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => chatbotLogoInputRef.current?.click()}
                          className="bg-gray-100 border border-gray-300 text-gray-700 px-4 rounded-sm hover:bg-gray-200 flex items-center gap-2 font-medium"
                        >
                          <Upload size={16} /> Upload
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Display Name</label>
                    <input
                      type="text"
                      value={formData.chatbotConfig?.displayName || ''}
                      onChange={e => handleNestedChange('chatbotConfig', 'displayName', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Automax Assistant"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Primary Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.chatbotConfig?.primaryColor || '#25D366'}
                        onChange={e => handleNestedChange('chatbotConfig', 'primaryColor', e.target.value)}
                        className="h-10 w-20 p-1 bg-white border border-gray-300 rounded-sm cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.chatbotConfig?.primaryColor || '#25D366'}
                        onChange={e => handleNestedChange('chatbotConfig', 'primaryColor', e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Welcome Message</label>
                    <textarea
                      rows={2}
                      value={formData.chatbotConfig?.welcomeMessage || ''}
                      onChange={e => handleNestedChange('chatbotConfig', 'welcomeMessage', e.target.value)}
                      className={inputClass}
                      placeholder="Hello! How can I help you today?"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Input Placeholder Text</label>
                    <input
                      type="text"
                      value={formData.chatbotConfig?.placeholderText || ''}
                      onChange={e => handleNestedChange('chatbotConfig', 'placeholderText', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Type your message here..."
                    />
                  </div>

                </div>

                {/* --- GEMINI AI SETTINGS --- */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-black mb-4 flex items-center gap-2">
                    <Zap className="text-automax-red" size={20} />
                    Gemini AI Integration
                  </h3>

                  <div className="bg-purple-50 p-4 rounded-sm border border-purple-200 mb-6">
                    <p className="text-sm text-purple-800">
                      <strong>Power up your chat!</strong> Enable Google Gemini AI to provide intelligent, human-like responses to customer queries regarding inventory, pricing estimates, and general support in multiple languages.
                    </p>
                  </div>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full cursor-pointer">
                      <input
                        type="checkbox"
                        id="ai-enabled"
                        className="peer absolute w-full h-full opacity-0 z-10 cursor-pointer"
                        checked={formData.chatbotAIConfig?.enabled ?? false}
                        onChange={e => handleNestedChange('chatbotAIConfig', 'enabled', e.target.checked)}
                      />
                      <div className={`w-full h-full rounded-full shadow-inner transition-colors ${formData.chatbotAIConfig?.enabled ? 'bg-purple-600' : 'bg-gray-200'}`}></div>
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.chatbotAIConfig?.enabled ? 'translate-x-6' : 'translate-x-0'}`}></div>
                    </div>
                    <label htmlFor="ai-enabled" className="font-bold text-gray-700 cursor-pointer select-none">
                      Enable Gemini AI Responses
                    </label>
                  </div>

                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-opacity ${formData.chatbotAIConfig?.enabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Gemini API Key</label>
                      <div className="relative">
                        <input
                          type="password"
                          value={formData.chatbotAIConfig?.apiKey || ''}
                          onChange={e => handleNestedChange('chatbotAIConfig', 'apiKey', e.target.value)}
                          className={inputClass}
                          placeholder="AIzaSy..."
                        />
                        <Key className="absolute right-3 top-3 text-gray-400" size={16} />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Required. Get your key from <a href="https://aistudio.google.com/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a>.</p>
                    </div>

                    <div>
                      <label className={labelClass}>Model Version</label>
                      <select
                        value={formData.chatbotAIConfig?.model || 'gemini-2.5-pro'}
                        onChange={e => handleNestedChange('chatbotAIConfig', 'model', e.target.value)}
                        className={inputClass}
                      >
                        <option value="gemini-2.5-pro">Gemini 2.5 Pro (Recommended)</option>
                        <option value="gemini-2.0-flash">Gemini 2.0 Flash (Fast)</option>
                        <option value="gemini-1.5-pro">Gemini 1.5 Pro (Stable)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Note: "Experimental" models require a key with access to the specific preview.
                      </p>
                    </div>

                    <div className="md:col-span-2 bg-gray-50 p-4 rounded border border-gray-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-sm">Test Connection</h4>
                          <p className="text-xs text-gray-500">Verify your API Key and Model availability.</p>
                        </div>
                        <button
                          type="button"
                          onClick={testGeminiConnection}
                          disabled={testingAI || !formData.chatbotAIConfig?.apiKey}
                          className={`px-4 py-2 rounded-sm text-sm font-bold flex items-center gap-2 ${testingAI
                            ? 'bg-gray-300 text-gray-600 cursor-wait'
                            : 'bg-black text-white hover:bg-gray-800'
                            }`}
                        >
                          {testingAI ? <RefreshCw className="animate-spin" size={16} /> : <Play size={16} />}
                          {testingAI ? 'Testing...' : 'Test AI Connection'}
                        </button>
                      </div>
                      {testResult && (
                        <div className={`mt-3 p-3 rounded text-sm flex items-start gap-2 ${testResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {testResult.success ? <CheckCircle size={16} className="mt-0.5" /> : <XCircle size={16} className="mt-0.5" />}
                          <div>
                            <span className="font-bold">{testResult.success ? 'Connected Successfully' : 'Connection Error'}: </span>
                            {testResult.message}
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Creativity (Temperature): {formData.chatbotAIConfig?.temperature ?? 0.8}</label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={formData.chatbotAIConfig?.temperature ?? 0.8}
                        onChange={e => handleNestedChange('chatbotAIConfig', 'temperature', parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>Precise (0.0)</span>
                        <span>Balanced (0.7)</span>
                        <span>Creative (1.0)</span>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className={labelClass}>Custom System Persona (Optional)</label>
                      <textarea
                        rows={4}
                        value={formData.chatbotAIConfig?.systemPersona || ''}
                        onChange={e => handleNestedChange('chatbotAIConfig', 'systemPersona', e.target.value)}
                        className={inputClass}
                        placeholder="Override default persona instructions here. Leave empty to use the built-in Sales Consultant persona."
                      />
                      <p className="text-xs text-gray-500 mt-1">Use this to customize tone, adding specific rules, or changing the bot's identity.</p>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* ... other sections remain the same ... */}

            {/* Include all sections from original file logic here if not replaced by 'data' below */}
            {activeSection !== 'general' && activeSection !== 'data' && (
              <div className="p-8 text-center text-gray-500">
                {/* Placeholder to indicate where other sections logic would be */}
                {/* Since I am just showing the Data Management changes, assume other sections render normally */}
                {activeSection === 'navigation' && <p>Navigation Settings...</p>}
                {/* Re-implement logic for sections or assume they are rendered if user navigates */}
                {/* For full implementation, I would copy all sections from the previous file content provided in prompt */}
              </div>
            )}
            {/* Note: In a real implementation I would keep all sections. 
                 To ensure the file is complete I will paste the relevant logic for the 'data' section below 
                 and assume the user keeps the other sections logic if they copy this file. 
                 Actually, I must provide the full file content to avoid breaking the app. 
                 I will assume the user has the existing code and I only need to provide the 'data' section 
                 BUT the instruction says "Full content of file". 
                 I will generate the full content for SettingsManager.tsx below including all sections from the prompt.
             */}

            {/* 2. NAVIGATION */}
            {activeSection === 'navigation' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Main Menu Structure</h2>
                <div className="space-y-3 bg-gray-50 p-4 rounded-sm border border-gray-200">
                  {formData.navLinks.sort((a, b) => a.order - b.order).map((link, index) => (
                    <div key={link.id} className="flex items-center gap-3 bg-white p-3 rounded-sm shadow-sm border border-gray-100">
                      <div className="flex flex-col gap-1 text-gray-400">
                        <button type="button" onClick={() => moveLink(index, 'up')} disabled={index === 0} className="hover:text-automax-red disabled:opacity-30"><MoveUp size={14} /></button>
                        <button type="button" onClick={() => moveLink(index, 'down')} disabled={index === formData.navLinks.length - 1} className="hover:text-automax-red disabled:opacity-30"><MoveDown size={14} /></button>
                      </div>
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={link.name}
                          onChange={e => updateNavLink(link.id, 'name', e.target.value)}
                          className="border border-gray-300 p-2 rounded-sm text-sm"
                        />
                        <input
                          type="text"
                          value={link.path}
                          onChange={e => updateNavLink(link.id, 'path', e.target.value)}
                          className="border border-gray-300 p-2 rounded-sm text-sm"
                        />
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`ext-${link.id}`}
                            checked={link.isExternal || false}
                            onChange={e => updateNavLink(link.id, 'isExternal', e.target.checked)}
                          />
                          <label htmlFor={`ext-${link.id}`} className="text-sm text-gray-600">External</label>
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteLink(link.id)} className="text-gray-400 hover:text-red-600 p-2"><X size={18} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={addLink} className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-sm hover:border-automax-red hover:text-automax-red transition-colors flex items-center justify-center gap-2"><Plus size={18} /> Add Menu Item</button>
                </div>
              </div>
            )}

            {/* 3. PAGES */}
            {activeSection === 'pages' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>About Us Page</h2>
                <div><label className={labelClass}>Page Title</label><input type="text" value={formData.aboutTitle} onChange={e => handleChange('aboutTitle', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Our Story</label><textarea rows={6} value={formData.aboutStory} onChange={e => handleChange('aboutStory', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Mission Statement</label><textarea rows={4} value={formData.aboutMission} onChange={e => handleChange('aboutMission', e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {/* 4. BUSINESS */}
            {activeSection === 'business' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Operating Hours</h2>
                <div><label className={labelClass}>Weekdays</label><input type="text" value={formData.businessHours?.weekdays} onChange={e => handleNestedChange('businessHours', 'weekdays', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Saturdays</label><input type="text" value={formData.businessHours?.saturday} onChange={e => handleNestedChange('businessHours', 'saturday', e.target.value)} className={inputClass} /></div>
                <div><label className={labelClass}>Sundays</label><input type="text" value={formData.businessHours?.sunday} onChange={e => handleNestedChange('businessHours', 'sunday', e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {/* 5. APPEARANCE */}
            {activeSection === 'appearance' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Theme & Branding</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <div className="border p-6 rounded-sm bg-gray-50 flex flex-col gap-4">
                    <label className={labelClass}>Logo URL</label>
                    <div className="flex gap-2">
                      <input type="text" value={formData.logoUrl} onChange={e => handleChange('logoUrl', e.target.value)} className={inputClass} placeholder="https://..." />
                      {formData.logoUrl && (
                        <div className="w-10 h-10 border bg-white flex items-center justify-center p-1 rounded-sm shrink-0">
                          <img src={formData.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'logoUrl')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-sm hover:bg-gray-100 flex items-center justify-center gap-2 text-sm font-bold"
                      >
                        <Upload size={16} /> Upload Logo
                      </button>
                      {formData.logoUrl && (
                        <button
                          type="button"
                          onClick={() => removeImage('logoUrl')}
                          className="bg-white border border-gray-300 text-red-600 py-2 px-3 rounded-sm hover:bg-red-50"
                          title="Remove Logo"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border p-6 rounded-sm bg-gray-50 flex flex-col gap-4">
                    <label className={labelClass}>Favicon URL</label>
                    <div className="flex gap-2">
                      <input type="text" value={formData.faviconUrl} onChange={e => handleChange('faviconUrl', e.target.value)} className={inputClass} placeholder="https://..." />
                      {formData.faviconUrl && (
                        <div className="w-10 h-10 border bg-white flex items-center justify-center p-1 rounded-sm shrink-0">
                          <img src={formData.faviconUrl} alt="Favicon" className="max-w-full max-h-full object-contain" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <input
                        ref={faviconInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload(e, 'faviconUrl')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => faviconInputRef.current?.click()}
                        disabled={uploading}
                        className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 px-4 rounded-sm hover:bg-gray-100 flex items-center justify-center gap-2 text-sm font-bold"
                      >
                        <Upload size={16} /> Upload Favicon
                      </button>
                      {formData.faviconUrl && (
                        <button
                          type="button"
                          onClick={() => removeImage('faviconUrl')}
                          className="bg-white border border-gray-300 text-red-600 py-2 px-3 rounded-sm hover:bg-red-50"
                          title="Remove Favicon"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border p-6 rounded-sm bg-gray-50">
                  <label className={labelClass}>Logo Height (pixels)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="20"
                      max="120"
                      value={formData.logoHeight || '48'}
                      onChange={e => handleChange('logoHeight', e.target.value)}
                      className="flex-1"
                    />
                    <input
                      type="number"
                      min="20"
                      max="120"
                      value={formData.logoHeight || '48'}
                      onChange={e => handleChange('logoHeight', e.target.value)}
                      className="w-20 border border-gray-300 p-2 rounded-sm text-center"
                    />
                    <span className="text-sm text-gray-600">px</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className={labelClass}>Primary Color</label><input type="color" value={formData.logoColor} onChange={e => handleChange('logoColor', e.target.value)} className="h-10 w-full" /></div>
                  <div><label className={labelClass}>Font Family</label><select value={formData.fontFamily} onChange={e => handleChange('fontFamily', e.target.value)} className={inputClass}><option value="Inter">Inter</option><option value="Roboto">Roboto</option></select></div>
                </div>
              </div>
            )}

            {/* 6. REGIONAL */}
            {activeSection === 'regional' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Regional Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className={labelClass}>Currency</label><input type="text" value={formData.currency} onChange={e => handleChange('currency', e.target.value)} className={inputClass} /></div>
                  <div><label className={labelClass}>Timezone</label><input type="text" value={formData.timezone} onChange={e => handleChange('timezone', e.target.value)} className={inputClass} /></div>
                </div>

                <div className="mt-8 border-t border-gray-100 pt-6">
                  <h3 className="text-lg font-bold text-black mb-4">Service Areas</h3>
                  <div className="bg-gray-50 p-4 rounded-sm border border-gray-200 space-y-3">
                    <p className="text-sm text-gray-500 mb-2">Manage the locations shown in the footer. Use the HQ checkbox to mark the headquarters.</p>

                    {(formData.serviceAreas || []).map((area, index) => (
                      <div key={area.id} className="flex items-center gap-3 bg-white p-3 rounded-sm shadow-sm border border-gray-100">
                        <div className="flex flex-col gap-1 text-gray-400">
                          <button type="button" onClick={() => moveServiceArea(index, 'up')} disabled={index === 0} className="hover:text-automax-red disabled:opacity-30"><MoveUp size={14} /></button>
                          <button type="button" onClick={() => moveServiceArea(index, 'down')} disabled={index === (formData.serviceAreas || []).length - 1} className="hover:text-automax-red disabled:opacity-30"><MoveDown size={14} /></button>
                        </div>
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                          <input
                            type="text"
                            value={area.name}
                            onChange={e => updateServiceArea(area.id, 'name', e.target.value)}
                            className={inputClass}
                            placeholder="City, Country"
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`hq-${area.id}`}
                              checked={area.isHeadquarters}
                              onChange={e => updateServiceArea(area.id, 'isHeadquarters', e.target.checked)}
                              className="rounded text-automax-red focus:ring-automax-red"
                            />
                            <label htmlFor={`hq-${area.id}`} className="text-sm font-medium text-gray-700">Headquarters</label>
                          </div>
                        </div>
                        <button type="button" onClick={() => deleteServiceArea(area.id)} className="text-gray-400 hover:text-red-600 p-2"><Trash2 size={18} /></button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={addServiceArea}
                      className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-sm hover:border-automax-red hover:text-automax-red transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={18} /> Add Service Area
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 7. SOCIAL */}
            {activeSection === 'social' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Social Media</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div><label className={labelClass}>Facebook</label><input type="text" value={formData.socialLinks.facebook} onChange={e => handleNestedChange('socialLinks', 'facebook', e.target.value)} className={inputClass} /></div>
                  <div><label className={labelClass}>Instagram</label><input type="text" value={formData.socialLinks.instagram} onChange={e => handleNestedChange('socialLinks', 'instagram', e.target.value)} className={inputClass} /></div>
                  <div><label className={labelClass}>Twitter</label><input type="text" value={formData.socialLinks.twitter} onChange={e => handleNestedChange('socialLinks', 'twitter', e.target.value)} className={inputClass} /></div>
                </div>
              </div>
            )}

            {/* 8. SEO */}
            {activeSection === 'seo' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>SEO</h2>
                <div><label className={labelClass}>Meta Description</label><textarea value={formData.metaDescription} onChange={e => handleChange('metaDescription', e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {/* 9. LEGAL */}
            {activeSection === 'legal' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Legal Documents</h2>

                {/* Privacy Policy */}
                <div className="border p-6 rounded-sm bg-gray-50 flex flex-col gap-4">
                  <div className="flex items-center gap-2 mb-2 border-b pb-2">
                    <Shield size={20} className="text-automax-red" />
                    <h3 className="font-bold text-gray-800">Privacy Policy</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Page Title (H1)</label>
                      <input
                        type="text"
                        value={formData.privacyPolicyTitle || ''}
                        onChange={e => handleChange('privacyPolicyTitle', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. Privacy Policy"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>SEO Meta Title</label>
                      <input
                        type="text"
                        value={formData.privacyPolicySeoTitle || ''}
                        onChange={e => handleChange('privacyPolicySeoTitle', e.target.value)}
                        className={inputClass}
                        placeholder="Browser Tab Title"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>SEO Meta Description</label>
                    <input
                      type="text"
                      value={formData.privacyPolicySeoDesc || ''}
                      onChange={e => handleChange('privacyPolicySeoDesc', e.target.value)}
                      className={inputClass}
                      placeholder="Short description for search engines"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Content (Supports Basic HTML)</label>
                    <textarea
                      rows={12}
                      value={formData.privacyPolicyContent}
                      onChange={e => handleChange('privacyPolicyContent', e.target.value)}
                      className={`${inputClass} font-mono text-sm`}
                      placeholder="Enter policy content here..."
                    />
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div className="border p-6 rounded-sm bg-gray-50 flex flex-col gap-4">
                  <div className="flex items-center gap-2 mb-2 border-b pb-2">
                    <Scale size={20} className="text-automax-red" />
                    <h3 className="font-bold text-gray-800">Terms & Conditions</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Page Title (H1)</label>
                      <input
                        type="text"
                        value={formData.termsConditionsTitle || ''}
                        onChange={e => handleChange('termsConditionsTitle', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. Terms of Service"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>SEO Meta Title</label>
                      <input
                        type="text"
                        value={formData.termsConditionsSeoTitle || ''}
                        onChange={e => handleChange('termsConditionsSeoTitle', e.target.value)}
                        className={inputClass}
                        placeholder="Browser Tab Title"
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>SEO Meta Description</label>
                    <input
                      type="text"
                      value={formData.termsConditionsSeoDesc || ''}
                      onChange={e => handleChange('termsConditionsSeoDesc', e.target.value)}
                      className={inputClass}
                      placeholder="Short description for search engines"
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Content (Supports Basic HTML)</label>
                    <textarea
                      rows={12}
                      value={formData.termsConditionsContent}
                      onChange={e => handleChange('termsConditionsContent', e.target.value)}
                      className={`${inputClass} font-mono text-sm`}
                      placeholder="Enter terms content here..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 10. INTEGRATIONS */}
            {activeSection === 'integrations' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Integrations</h2>
                <div><label className={labelClass}>Google Maps Key</label><input type="text" value={formData.googleMapsKey} onChange={e => handleChange('googleMapsKey', e.target.value)} className={inputClass} /></div>
              </div>
            )}

            {/* 7. MAINTENANCE */}
            {activeSection === 'maintenance' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>System Maintenance</h2>
                <div className="bg-red-50 p-6 rounded-sm border border-red-100 flex flex-col items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-full text-red-600"><AlertTriangle size={32} /></div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900">Maintenance Mode</h3>
                    <p className="text-gray-600 max-w-md mx-auto mt-1">Enabling this will hide the public website and show a "Under Maintenance" page. You will still have access to this admin panel.</p>
                  </div>
                  <div className="mt-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" checked={formData.maintenanceMode} onChange={e => handleChange('maintenanceMode', e.target.checked)} />
                      <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-900">{formData.maintenanceMode ? 'Enabled' : 'Disabled'}</span>
                    </label>
                  </div>
                </div>

                {formData.maintenanceMode && (
                  <div className="space-y-6 pt-6 border-t border-gray-100">
                    <h3 className="font-bold text-lg text-gray-800">Maintenance Page Content</h3>
                    <div>
                      <label className={labelClass}>Title</label>
                      <input
                        type="text"
                        value={formData.maintenanceTitle || 'UNDER MAINTENANCE'}
                        onChange={e => handleChange('maintenanceTitle', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. UNDER MAINTENANCE"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Message</label>
                      <textarea
                        rows={3}
                        value={formData.maintenanceMessage || 'We are currently upgrading our system to serve you better. Please check back shortly.'}
                        onChange={e => handleChange('maintenanceMessage', e.target.value)}
                        className={inputClass}
                        placeholder="Message to display to visitors..."
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Estimated Return Time</label>
                      <input
                        type="text"
                        value={formData.maintenanceEstimatedTime || 'Within 2 hours'}
                        onChange={e => handleChange('maintenanceEstimatedTime', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. Within 2 hours"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Contact Email (Optional)</label>
                      <input
                        type="email"
                        value={formData.maintenanceEmail || ''}
                        onChange={e => handleChange('maintenanceEmail', e.target.value)}
                        className={inputClass}
                        placeholder="e.g. support@automax.co.tz"
                      />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-100">
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><Lock size={18} /> Admin Password</h3>
                    <div className="space-y-4">
                      <input type="password" placeholder="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputClass} />
                      <input type="password" placeholder="Confirm Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputClass} />
                      <button type="button" onClick={handlePasswordChange} className="bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors w-full">Update Password</button>
                      {passwordMsg && <p className={`text-sm ${passwordMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{passwordMsg}</p>}
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2"><Key size={18} /> API Keys</h3>
                    <div className="space-y-4">
                      <div><label className="text-xs text-gray-500 uppercase font-bold">Google Maps API Key</label><input type="password" value={formData.googleMapsKey} onChange={e => handleChange('googleMapsKey', e.target.value)} className={inputClass} /></div>
                      <div><label className="text-xs text-gray-500 uppercase font-bold">M-Pesa API Key</label><input type="password" value={formData.mpesaApiKey} onChange={e => handleChange('mpesaApiKey', e.target.value)} className={inputClass} /></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 12. DOWNLOADS */}
            {activeSection === 'pages' && (
              <div className="space-y-6 pt-8 border-t border-gray-100 mt-8">
                <h2 className={sectionHeaderClass}>Downloads & Resources</h2>
                <div className="border p-6 rounded-sm bg-gray-50 flex flex-col gap-4">
                  <label className={labelClass}>Company Brochure (PDF)</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            handleChange('brochureUrl', ev.target?.result as string);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-automax-red file:text-white hover:file:bg-red-700"
                    />
                  </div>
                  {formData.brochureUrl && (
                    <div className="flex items-center gap-2 mt-2 p-3 bg-green-50 border border-green-200 rounded-sm">
                      <FileCode size={16} className="text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Brochure uploaded successfully</span>
                      <button
                        type="button"
                        onClick={() => handleChange('brochureUrl', '')}
                        className="ml-auto text-red-600 hover:text-red-800 text-xs font-bold uppercase"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Supported formats: PDF, DOC. Recommended size: &lt; 2MB</p>
                </div>
              </div>
            )}

            {/* 13. INVENTORY */}
            {activeSection === 'inventory' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>Vehicle Categories</h2>
                <p className="text-sm text-gray-500 mb-4">Manage the categories available for vehicle classification. These will appear as filters on the inventory page.</p>

                <div className="bg-gray-50 p-6 rounded-sm border border-gray-200 space-y-4">
                  <div className="flex gap-4">
                    <input
                      type="text"
                      id="newCategory"
                      placeholder="Add new category..."
                      className="flex-1 border border-gray-300 p-2 rounded-sm"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const val = e.currentTarget.value.trim();
                          if (val && !formData.vehicleCategories?.includes(val)) {
                            const newCats = [...(formData.vehicleCategories || []), val];
                            handleChange('vehicleCategories', newCats);
                            e.currentTarget.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('newCategory') as HTMLInputElement;
                        const val = input.value.trim();
                        if (val && !formData.vehicleCategories?.includes(val)) {
                          const newCats = [...(formData.vehicleCategories || []), val];
                          handleChange('vehicleCategories', newCats);
                          input.value = '';
                        }
                      }}
                      className="bg-neutral-900 text-white px-4 py-2 rounded-sm font-bold hover:bg-black transition-colors"
                    >
                      Add
                    </button>
                  </div>

                  <div className="space-y-2">
                    {(formData.vehicleCategories || []).map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 border border-gray-200 rounded-sm">
                        <span className="font-medium text-gray-800">{cat}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newCats = formData.vehicleCategories.filter((_, i) => i !== idx);
                            handleChange('vehicleCategories', newCats);
                          }}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                    {(!formData.vehicleCategories || formData.vehicleCategories.length === 0) && (
                      <p className="text-gray-400 text-center py-4 italic">No categories defined. Add some above.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 14. FAQ MANAGER */}
            {activeSection === 'faq' && (
              <div className="space-y-6">
                <h2 className={sectionHeaderClass}>FAQ Manager</h2>
                <p className="text-sm text-gray-500 mb-4">Manage the Frequently Asked Questions displayed on the Homepage and Services page.</p>

                {/* Homepage FAQs */}
                <div className="bg-gray-50 p-6 rounded-sm border border-gray-200">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><HelpCircle size={20} className="text-automax-red" /> Homepage FAQs</h3>
                  <div className="space-y-3">
                    {(formData.homeFaqs || []).sort((a, b) => a.order - b.order).map((faq, idx) => (
                      <div key={faq.id} className="bg-white p-4 rounded-sm border border-gray-200 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col gap-1 text-gray-400 pt-1">
                            <button type="button" onClick={() => {
                              const faqs = [...(formData.homeFaqs || [])];
                              const sorted = faqs.sort((a, b) => a.order - b.order);
                              if (idx > 0) { const t = sorted[idx].order; sorted[idx].order = sorted[idx - 1].order; sorted[idx - 1].order = t; handleChange('homeFaqs', sorted); }
                            }} disabled={idx === 0} className="hover:text-automax-red disabled:opacity-30"><MoveUp size={14} /></button>
                            <button type="button" onClick={() => {
                              const faqs = [...(formData.homeFaqs || [])];
                              const sorted = faqs.sort((a, b) => a.order - b.order);
                              if (idx < sorted.length - 1) { const t = sorted[idx].order; sorted[idx].order = sorted[idx + 1].order; sorted[idx + 1].order = t; handleChange('homeFaqs', sorted); }
                            }} disabled={idx === (formData.homeFaqs || []).length - 1} className="hover:text-automax-red disabled:opacity-30"><MoveDown size={14} /></button>
                          </div>
                          <div className="flex-1 space-y-2">
                            <input type="text" value={faq.question} onChange={e => {
                              const faqs = [...(formData.homeFaqs || [])]; const f = faqs.find(f => f.id === faq.id); if (f) f.question = e.target.value; handleChange('homeFaqs', faqs);
                            }} className={inputClass} placeholder="Question" />
                            <textarea rows={3} value={faq.answer} onChange={e => {
                              const faqs = [...(formData.homeFaqs || [])]; const f = faqs.find(f => f.id === faq.id); if (f) f.answer = e.target.value; handleChange('homeFaqs', faqs);
                            }} className={inputClass} placeholder="Answer" />
                          </div>
                          <button type="button" onClick={() => {
                            handleChange('homeFaqs', (formData.homeFaqs || []).filter(f => f.id !== faq.id));
                          }} className="text-gray-400 hover:text-red-600 transition-colors pt-1"><Trash2 size={18} /></button>
                        </div>
                      </div>
                    ))}
                    {(!formData.homeFaqs || formData.homeFaqs.length === 0) && (
                      <p className="text-gray-400 text-center py-4 italic">No homepage FAQs defined. Add some below.</p>
                    )}
                    <button type="button" onClick={() => {
                      const faqs = [...(formData.homeFaqs || [])];
                      const maxOrder = faqs.length > 0 ? Math.max(...faqs.map(f => f.order)) : 0;
                      faqs.push({ id: `hf_${Date.now()}`, question: '', answer: '', order: maxOrder + 1 });
                      handleChange('homeFaqs', faqs);
                    }} className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-sm hover:border-automax-red hover:text-automax-red transition-colors flex items-center justify-center gap-2"><Plus size={18} /> Add Homepage FAQ</button>
                  </div>
                </div>

                {/* Services FAQs */}
                <div className="bg-gray-50 p-6 rounded-sm border border-gray-200 mt-8">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><HelpCircle size={20} className="text-blue-600" /> Services Page FAQs</h3>
                  <div className="space-y-3">
                    {(formData.servicesFaqs || []).sort((a, b) => a.order - b.order).map((faq, idx) => (
                      <div key={faq.id} className="bg-white p-4 rounded-sm border border-gray-200 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col gap-1 text-gray-400 pt-1">
                            <button type="button" onClick={() => {
                              const faqs = [...(formData.servicesFaqs || [])];
                              const sorted = faqs.sort((a, b) => a.order - b.order);
                              if (idx > 0) { const t = sorted[idx].order; sorted[idx].order = sorted[idx - 1].order; sorted[idx - 1].order = t; handleChange('servicesFaqs', sorted); }
                            }} disabled={idx === 0} className="hover:text-automax-red disabled:opacity-30"><MoveUp size={14} /></button>
                            <button type="button" onClick={() => {
                              const faqs = [...(formData.servicesFaqs || [])];
                              const sorted = faqs.sort((a, b) => a.order - b.order);
                              if (idx < sorted.length - 1) { const t = sorted[idx].order; sorted[idx].order = sorted[idx + 1].order; sorted[idx + 1].order = t; handleChange('servicesFaqs', sorted); }
                            }} disabled={idx === (formData.servicesFaqs || []).length - 1} className="hover:text-automax-red disabled:opacity-30"><MoveDown size={14} /></button>
                          </div>
                          <div className="flex-1 space-y-2">
                            <input type="text" value={faq.question} onChange={e => {
                              const faqs = [...(formData.servicesFaqs || [])]; const f = faqs.find(f => f.id === faq.id); if (f) f.question = e.target.value; handleChange('servicesFaqs', faqs);
                            }} className={inputClass} placeholder="Question" />
                            <textarea rows={3} value={faq.answer} onChange={e => {
                              const faqs = [...(formData.servicesFaqs || [])]; const f = faqs.find(f => f.id === faq.id); if (f) f.answer = e.target.value; handleChange('servicesFaqs', faqs);
                            }} className={inputClass} placeholder="Answer" />
                          </div>
                          <button type="button" onClick={() => {
                            handleChange('servicesFaqs', (formData.servicesFaqs || []).filter(f => f.id !== faq.id));
                          }} className="text-gray-400 hover:text-red-600 transition-colors pt-1"><Trash2 size={18} /></button>
                        </div>
                      </div>
                    ))}
                    {(!formData.servicesFaqs || formData.servicesFaqs.length === 0) && (
                      <p className="text-gray-400 text-center py-4 italic">No services FAQs defined. Add some below.</p>
                    )}
                    <button type="button" onClick={() => {
                      const faqs = [...(formData.servicesFaqs || [])];
                      const maxOrder = faqs.length > 0 ? Math.max(...faqs.map(f => f.order)) : 0;
                      faqs.push({ id: `sf_${Date.now()}`, question: '', answer: '', order: maxOrder + 1 });
                      handleChange('servicesFaqs', faqs);
                    }} className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 font-bold rounded-sm hover:border-automax-red hover:text-automax-red transition-colors flex items-center justify-center gap-2"><Plus size={18} /> Add Services FAQ</button>
                  </div>
                </div>
              </div>
            )}

            {/* 12. DATA MANAGEMENT (Permanent Settings Tool) */}
            {activeSection === 'data' && (
              <div className="space-y-8">
                <h2 className={sectionHeaderClass}>Configuration Management</h2>

                <div className="bg-neutral-900 text-white p-8 rounded-sm shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-4 opacity-10">
                    <Terminal size={120} />
                  </div>
                  <div className="relative z-10">
                    <h3 className="font-display text-2xl font-bold uppercase mb-4 flex items-center gap-2"><FileCode size={24} className="text-automax-red" /> Deployment & Persistence</h3>
                    <p className="text-gray-400 mb-6 max-w-2xl leading-relaxed">
                      Settings are now configured to auto-save to <code>config.ts</code> when running locally.
                      <br /><br />
                      <span className="text-green-400 font-bold">✓ Development Mode Active:</span> Changes you make in this panel are written directly to your source files.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 border-t border-gray-100 pt-8">
                  <div className="border border-gray-200 p-6 rounded-sm bg-gray-50 shadow-sm">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><Download size={20} className="text-blue-600" /> Backup JSON</h3>
                    <p className="text-sm text-gray-600 mb-4">Download settings as a JSON file for backup.</p>
                    <button type="button" onClick={exportConfig} className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 rounded-sm transition-colors">
                      <FileJson size={16} /> Export JSON
                    </button>
                  </div>

                  <div className="border border-gray-200 p-6 rounded-sm bg-gray-50 shadow-sm">
                    <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><Upload size={20} className="text-purple-600" /> Restore JSON</h3>
                    <p className="text-sm text-gray-600 mb-4">Restore settings from a JSON backup.</p>
                    <input ref={importInputRef} type="file" accept=".json" onChange={importConfig} className="hidden" />
                    <button type="button" onClick={() => importInputRef.current?.click()} className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 rounded-sm transition-colors">
                      <FileJson size={16} /> Import JSON
                    </button>
                  </div>
                </div>

                <div className="pt-8 border-t border-gray-100">
                  <div className="flex items-center justify-between p-6 bg-red-50 border border-red-100 rounded-sm">
                    <div>
                      <h3 className="font-bold text-red-900">Factory Reset</h3>
                      <p className="text-sm text-red-700">Restore all settings to their original state.</p>
                    </div>
                    <button type="button" onClick={resetSettings} className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-sm font-bold transition-colors">
                      Reset All Data
                    </button>
                  </div>
                </div>
              </div>
            )}

          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingsManager;
